import React, { useState, useEffect } from 'react';
import { Layout, Button, Typography, Space, Tooltip, Dropdown, Avatar, Breadcrumb } from 'antd';
import { MenuOutlined, LogoutOutlined, HomeOutlined } from '@ant-design/icons';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import Footer from '../Footer';
import Sidebar from '../../Sidebar';

const { Header, Content } = Layout;
const { Title, Text } = Typography;

const PAGE_TITLES = {
  home: { title: 'Dashboard Overview', subtitle: 'Real-time financial metrics, invoice history, and quick actions' },
  invoice: { title: 'Create Sales Invoice', subtitle: 'Generate and print custom sales invoices for customers' },
  customer: { title: 'Customers Directory', subtitle: 'Manage client accounts, contact details, and balances' },
  product: { title: 'Products & Inventory', subtitle: 'Track stock levels, pricing, and product catalog' },
  psh: { title: 'Create Purchase Invoice', subtitle: 'Record inventory purchases and supplier bills' },
  addsupplier: { title: 'Supplier Directory', subtitle: 'Manage vendor profiles and purchase contact information' },
  purchaseinvoicehistory: { title: 'Purchase History', subtitle: 'Review and manage past supplier invoices' },
  salary: { title: 'Payroll & Salary Advances', subtitle: 'Log staff advance payments and payroll records' },
};

const Dashboard = () => {
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const currentPathKey = location.pathname.split('/').filter(Boolean)[1] || 'home';
  const pageMeta = PAGE_TITLES[currentPathKey] || PAGE_TITLES.home;

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    navigate('/login');
  };

  const handleMenuClick = (e) => {
    navigate(`/dashboard/${e.key}`);
    if (isMobile) setDrawerVisible(false);
  };

  const toggleDrawer = () => {
    if (isMobile) setDrawerVisible(!drawerVisible);
  };

  const userName = localStorage.getItem('userName') || 'Admin User';

  const userMenuItems = [
    {
      key: 'user-info',
      label: (
        <div style={{ padding: '4px 8px' }}>
          <Text strong style={{ display: 'block', fontSize: '14px', color: '#0F172A' }}>{userName}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>Administrator</Text>
        </div>
      ),
    },
    { type: 'divider' },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      danger: true,
    },
  ];

  const handleUserMenuClick = (e) => {
    if (e.key === 'logout') {
      handleLogout();
    }
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#F8FAFC' }}>
      <Sidebar 
        isMobile={isMobile}
        drawerVisible={drawerVisible}
        onDrawerClose={() => setDrawerVisible(false)}
        onMenuClick={handleMenuClick}
      />
      
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <Header style={{ 
          padding: isMobile ? '0 16px' : '0 32px', 
          background: '#ffffff', 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          boxShadow: '0 4px 20px -2px rgba(15, 23, 42, 0.04)',
          borderBottom: '1px solid #E2E8F0',
          height: 72,
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}>
          <Space size="middle">
            {isMobile && (
              <Tooltip title="Toggle Menu">
                <Button
                  type="text"
                  icon={<MenuOutlined style={{ fontSize: '18px', color: '#0F172A' }} />}
                  onClick={toggleDrawer}
                  style={{ borderRadius: '8px' }}
                />
              </Tooltip>
            )}
            <div>
              <Breadcrumb
                items={[
                  { title: <Space size={4}><HomeOutlined style={{ fontSize: '12px' }} /><span>Dashboard</span></Space> },
                  { title: pageMeta.title },
                ]}
                style={{ fontSize: '12px', marginBottom: '2px' }}
              />
              <Title level={4} style={{ margin: 0, color: '#0F172A', fontWeight: 700, fontSize: isMobile ? '16px' : '18px', letterSpacing: '-0.3px' }}>
                {pageMeta.title}
              </Title>
            </div>
          </Space>
          
          <Dropdown 
            menu={{ items: userMenuItems, onClick: handleUserMenuClick }}
            placement="bottomRight"
            trigger={['click']}
          >
            <Button 
              type="text" 
              style={{ 
                height: '46px',
                padding: '4px 12px',
                borderRadius: '12px',
                border: '1px solid #E2E8F0',
                background: '#ffffff',
                boxShadow: '0 2px 6px rgba(15, 23, 42, 0.03)'
              }}
            >
              <Space size="small">
                <Avatar 
                  style={{ 
                    background: 'linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)',
                    fontWeight: 700,
                    fontSize: '14px',
                    color: '#ffffff'
                  }}
                  size={32}
                >
                  {userName.charAt(0).toUpperCase()}
                </Avatar>
                {!isMobile && (
                  <div style={{ textAlign: 'left', lineHeight: '1.2' }}>
                    <Text strong style={{ display: 'block', fontSize: '13px', color: '#0F172A' }}>{userName}</Text>
                    <Text style={{ fontSize: '11px', color: '#64748B' }}>Owner</Text>
                  </div>
                )}
              </Space>
            </Button>
          </Dropdown>
        </Header>

        <Content style={{ 
          margin: isMobile ? '16px' : '24px 32px',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
        }}>
          <div className="fade-in" style={{ 
            padding: isMobile ? '16px' : '28px', 
            background: '#ffffff', 
            borderRadius: '16px',
            boxShadow: '0 4px 20px -2px rgba(15, 23, 42, 0.04)',
            border: '1px solid #E2E8F0',
            flex: 1,
            overflow: 'auto',
          }}>
            <Outlet />
          </div>
        </Content>

        <Footer />
      </div>
    </div>
  );
};

export default Dashboard;

